import type { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";

type ToastFn = ReturnType<typeof useToast>["toast"];

interface ApiErrorBody {
  error?: string;
  code?: string;
  message?: string;
}

interface KnownErrorMapping {
  title: string;
  description: string;
  variant?: "default" | "destructive";
}

const KNOWN_ERRORS: Record<string, KnownErrorMapping> = {
  locked_clauses: {
    title: "Locked clauses — Legal review required",
    description: "One or more locked clauses affect these terms. Open the Clauses tab and request a Legal review to continue.",
    variant: "destructive",
  },
  forbidden: {
    title: "Permission denied",
    description: "Your role doesn't allow this action. Ask an admin to elevate your permissions.",
    variant: "destructive",
  },
  invalid_transition: {
    title: "Action not allowed in this phase",
    description: "The contract isn't in a state where this action is permitted. Check the lifecycle phase and try again.",
    variant: "destructive",
  },
  validation_error: {
    title: "Some fields need attention",
    description: "Please fix the highlighted fields and try again.",
    variant: "destructive",
  },
  rate_limited: {
    title: "Too many attempts",
    description: "Slow down a moment, then try again.",
    variant: "destructive",
  },
  not_found: {
    title: "Not found",
    description: "We couldn't find what you were looking for. It may have been removed.",
    variant: "destructive",
  },
  conflict: {
    title: "Conflict",
    description: "Someone else may have changed this record. Refresh and try again.",
    variant: "destructive",
  },
  seat_limit_reached: {
    title: "Seat limit reached",
    description: "Your plan's user seat limit is full. Upgrade or remove a seat to continue.",
    variant: "destructive",
  },
  coach_limit_reached: {
    title: "Coach limit reached",
    description: "Your plan's coach limit is full. Upgrade your plan to add more.",
    variant: "destructive",
  },
  read_only_mode: {
    title: "Workspace is read-only",
    description: "Your workspace is in read-only mode. Reactivate your plan in Settings → Plans to continue editing.",
    variant: "destructive",
  },
  demo_mode: {
    title: "Not available in demo",
    description: "This action is disabled in the demo workspace. Sign up for full access.",
    variant: "destructive",
  },
  demo_feature_disabled: {
    title: "Feature locked in the demo",
    description: "This area is read-only in the sandbox. Sign up for full access.",
    variant: "destructive",
  },
  demo_session_expired: {
    title: "Your demo session has ended",
    description: "Demos are limited to 15 minutes. Sign up to keep your work.",
    variant: "destructive",
  },
  internal_approvals_pending: {
    title: "Internal approvals required",
    description: "This contract hasn't cleared internal review yet — Finance, HR, Legal and AD must sign off before it can be sent externally.",
    variant: "destructive",
  },
  already_out: {
    title: "Already out for negotiation",
    description: "This contract is already with the counterparty. Log a counter-proposal to continue the back-and-forth.",
    variant: "destructive",
  },
  invalid_lifecycle_for_send: {
    title: "Cannot send from this stage",
    description: "This contract has already moved into the signature phase and cannot be re-sent for negotiation. Manage signing in the Approval tab.",
    variant: "destructive",
  },
};

/** Hint URL + button label surfaced for known error codes. */
export interface ErrorRemediation {
  href: string;
  label: string;
}
export const ERROR_REMEDIATION_LINK: Record<string, ErrorRemediation> = {
  read_only_mode:        { href: "/settings#plans",          label: "Open Settings" },
  seat_limit_reached:    { href: "/settings#plans",          label: "Open Settings" },
  coach_limit_reached:   { href: "/settings#plans",          label: "Open Settings" },
  // For internal-approvals errors we don't know the contract id at this
  // central layer; call sites that do know it should pass an override
  // pointing at `/contracts/{id}#approval`.
  internal_approvals_pending: { href: "/contracts",          label: "View contracts" },
  demo_feature_disabled:      { href: "/sign-up",            label: "Sign up free" },
};

interface ExtractedError {
  code?: string;
  body?: ApiErrorBody;
  message?: string;
  status?: number;
}

function extractError(err: unknown): ExtractedError {
  if (!err) return {};
  // Orval error shape: { status, data: { error, message, code } }
  if (typeof err === "object") {
    const e = err as { status?: number; data?: ApiErrorBody; message?: string; response?: { status?: number; data?: ApiErrorBody } };
    const data = e.data ?? e.response?.data;
    const status = e.status ?? e.response?.status;
    const body = data ?? undefined;
    const code = body?.code ?? body?.error;
    const message = body?.message ?? body?.error ?? e.message;
    return { code, body, message, status };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: String(err) };
}

export interface HandleApiErrorOptions {
  fallbackTitle?: string;
  fallbackDescription?: string;
  /** Override mappings or add new ones for this call site. */
  overrides?: Record<string, KnownErrorMapping>;
  /** Override the remediation deep-link / button label per error code. */
  remediationOverrides?: Record<string, ErrorRemediation>;
}

/**
 * Centralized API error → toast helper.
 *
 * Inspects an error returned from the typed API client (Orval) or a raw fetch
 * response and dispatches an actionable toast. Returns the matched mapping so
 * callers can branch on the code if needed.
 */
export function handleApiError(
  toast: ToastFn,
  err: unknown,
  options: HandleApiErrorOptions = {},
): KnownErrorMapping {
  const extracted = extractError(err);
  const code = extracted.code;

  const mapping = (code && (options.overrides?.[code] ?? KNOWN_ERRORS[code])) || null;

  if (mapping) {
    // Per-call remediation overrides (e.g. context-correct contract URL)
    // win over the central default.
    const remediation: ErrorRemediation | undefined = code
      ? (options.remediationOverrides?.[code] ?? ERROR_REMEDIATION_LINK[code])
      : undefined;
    toast({
      title: mapping.title,
      description: extracted.body?.message ?? extracted.body?.error ?? mapping.description,
      variant: mapping.variant ?? "destructive",
      action: remediation ? (
        <ToastAction
          altText={remediation.label}
          asChild
          data-testid={`toast-remediation-${code}`}
        >
          <a href={remediation.href}>{remediation.label}</a>
        </ToastAction>
      ) : undefined,
    });
    return mapping;
  }

  const fallback: KnownErrorMapping = {
    title: options.fallbackTitle ?? "Something went wrong",
    description:
      extracted.message ??
      options.fallbackDescription ??
      "An unexpected error occurred. Please try again.",
    variant: "destructive",
  };
  toast({ title: fallback.title, description: fallback.description, variant: "destructive" });
  return fallback;
}
